import type { FieldErrors } from "react-hook-form";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import type {
  ShopEntity,
  ShopFooterDesign,
  ShopHeaderDesign,
  ShopSectionEntity,
  ShopSectionType,
} from "../../../../domain/types/company-shop";
import {
  CUSTOM_PRESET_KEY,
  FALLBACK_SECTION_ORDER,
  FOOTER_PRESETS,
  HEADER_PRESETS,
  type FooterPresetKey,
  type HeaderPresetKey,
} from "./constants";

/**
 * react-hook-form 검증 실패 시 첫 번째 에러만 콘솔에 남긴다.
 * MallDesign 안의 배너 폼들이 공유한다.
 */
export const onError = (errors: FieldErrors<FormData>) => {
  const keys = Object.keys(errors) as (keyof FormData)[];

  for (const key of keys) {
    const error = errors[key];
    if (error) {
      console.error(error);
      break;
    }
  }
};

/**
 * 구버전 백엔드 응답에는 consultEnabled 가 없다. 그때는 예전 의미(채널이 NONE 이 아니면 켜짐)로 채운다.
 * 채워 두지 않으면 저장할 때 false 가 실려 가 멀쩡한 상담 버튼이 꺼진다.
 */
export const normalizeConsultEnabled = (shop: ShopEntity): ShopEntity => ({
  ...shop,
  consultEnabled: shop.consultEnabled ?? shop.consultChannelType !== "NONE",
});

/** shops 테이블의 기존 on/off 불리언 중 섹션 하나에 대응하는 것들 */
type ShopSectionFlag =
  | "stripBannerEnabled"
  | "recentlyViewedEnabled"
  | "bestSellerEnabled";

/**
 * 섹션 on/off 는 두 군데(기존 shops 불리언 / 신규 shop_sections.is_enabled)에
 * 존재한다. 백엔드는 shop_sections 를 단일 진실로 삼고 불리언을 파생시키지만,
 * MES 는 백엔드 배포 전까지 기존 불리언으로도 저장해야 하므로 양쪽을 동기화한다.
 *
 * BANNER 는 여기 없다. 쇼핑몰 index.ejs 의 배너 섹션은 isCarouselLayout 으로
 * 레이아웃만 분기할 뿐 노출을 막는 조건이 없어서, 예전의 designProductEnabled
 * 는 쇼핑몰 렌더링에 영향이 없는 죽은 플래그였다(그래서 제거했다).
 * 섹션 seed 도 TRUE 로 고정이므로 배너의 on/off 는 섹션 값이 단일 진실이다.
 *
 * PORTFOLIO 도 의도적으로 여기 없다. shops.portfolio_enabled 가 존재하지만
 * 그건 **헤더 메뉴 노출 전용**이고, 메인 섹션 노출은 shop_sections.is_enabled
 * 가 단일 진실이다(F-8 결정 4). 둘을 동기화하면 "메뉴에는 두되 메인에는 안
 * 띄우기"가 불가능해진다. 마이그레이션 시점에만 값을 복사해 화면이 바뀌지
 * 않게 하고, 그 뒤로는 각자 움직인다.
 */
export const SECTION_TYPE_TO_SHOP_FLAG: Partial<
  Record<ShopSectionType, ShopSectionFlag>
> = {
  STRIP_BANNER: "stripBannerEnabled",
  RECENTLY_VIEWED: "recentlyViewedEnabled",
  BEST_SELLER: "bestSellerEnabled",
};

/** 백엔드에서 받아온 적 없는, 프론트가 합성한 섹션인지 */
export const isFallbackSection = (section: ShopSectionEntity): boolean =>
  section.id < 0;

/**
 * GET /shop/section 이 아직 없을 때(백엔드 미배포) 쓸 기본 섹션 배열.
 * 백엔드 seed 와 같은 순서 · 같은 is_enabled 소스를 쓰므로,
 * 합성 결과가 지금 화면과 정확히 일치한다.
 *
 * id 는 실제 PK 와 섞이지 않도록 음수를 준다.
 */
export const buildFallbackSections = (
  shop: ShopEntity | null,
  /**
   * 포트폴리오 섹션의 seed 소스. 백엔드 마이그레이션이
   * shop_sections.PORTFOLIO.is_enabled 를 shops.portfolio_enabled 로 채우므로,
   * 합성 결과를 seed 와 일치시키려면 같은 값을 받아야 한다.
   * 아직 못 읽었으면 false 로 둔다 - 없던 블록을 임의로 켜 보이는 것보다
   * 안 보이는 쪽이 현재 화면에 가깝다.
   */
  portfolioEnabled = false,
): ShopSectionEntity[] =>
  FALLBACK_SECTION_ORDER.map((sectionType, index) => {
    const flag = SECTION_TYPE_TO_SHOP_FLAG[sectionType];

    // BANNER / ALL_PRODUCTS 는 대응 불리언이 없고 seed 가 TRUE 다
    const isEnabled =
      sectionType === "PORTFOLIO"
        ? portfolioEnabled
        : flag
          ? (shop?.[flag] ?? false)
          : true;

    return {
      id: -(index + 1),
      sectionType,
      displayOrder: index,
      isEnabled,
      title: null,
      subtitle: null,
      cardShape: "DEFAULT" as const,
      layoutType: "GRID" as const,
      columnCount: 4,
      products: [],
      portfolios: [],
    };
  });

/**
 * 서버에서 받은 섹션의 is_enabled 를 shops 불리언 쪽 값으로 덮어쓴다.
 * 좌측 패널의 기존 토글(배너/띠배너/최근 둘러본/베스트셀러)이 여전히
 * shopEntityData 를 직접 수정하기 때문에, 미리보기가 그걸 따라가게 하려면 필요하다.
 */
export const syncSectionsWithShopFlags = (
  sections: ShopSectionEntity[],
  shop: ShopEntity | null,
): ShopSectionEntity[] =>
  sections.map((section) => {
    const flag = SECTION_TYPE_TO_SHOP_FLAG[section.sectionType];
    if (!flag || !shop) return section;

    return { ...section, isEnabled: shop[flag] };
  });

/** displayOrder 를 배열 인덱스로 다시 매긴다 (드래그 직후 호출) */
export const renumberSections = (
  sections: ShopSectionEntity[],
): ShopSectionEntity[] =>
  sections.map((section, index) => ({ ...section, displayOrder: index }));

/** 프리셋 값을 개별 필드로 복사한다 (참조가 아니라 복사) */
export const applyHeaderPreset = (
  design: ShopHeaderDesign,
  presetKey: HeaderPresetKey,
): ShopHeaderDesign => ({
  ...design,
  ...HEADER_PRESETS[presetKey],
  presetKey,
});

export const applyFooterPreset = (
  design: ShopFooterDesign,
  presetKey: FooterPresetKey,
): ShopFooterDesign => ({
  ...design,
  ...FOOTER_PRESETS[presetKey],
  presetKey,
});

/**
 * 개별 필드를 수정하면 더 이상 프리셋 그대로가 아니므로 presetKey 를 CUSTOM 으로 내린다.
 * 메뉴 항목 편집도 마찬가지다.
 */
export const patchHeaderDesign = (
  design: ShopHeaderDesign,
  patch: Partial<ShopHeaderDesign>,
): ShopHeaderDesign => ({
  ...design,
  ...patch,
  presetKey: CUSTOM_PRESET_KEY,
});

export const patchFooterDesign = (
  design: ShopFooterDesign,
  patch: Partial<ShopFooterDesign>,
): ShopFooterDesign => ({
  ...design,
  ...patch,
  presetKey: CUSTOM_PRESET_KEY,
});

/*
  저장 흐름 도구는 shared/utils/save-flow 로 옮겼다. MallDesign 안의
  기존 import 경로를 깨지 않으려고 여기서 재수출한다.
*/
export {
  resolveErrorMessage,
  runSaveSteps,
  buildSaveResultMessage,
} from "../../../../shared/utils/save-flow";
export type {
  SaveStep,
  SaveStepsResult,
} from "../../../../shared/utils/save-flow";
